const DUPLICATE_EMAIL_ERROR_PATTERN =
  /taken|already|in use|registered|exists|has already been/i;

const VERIFY_EMAIL_ERROR_PATTERN = /must be verified|verify email|verification/i;

const CHALLENGE_ERROR_PATTERN =
  /challenge verification|spam protection|recaptcha|captcha|security verification/i;

const SUBMITTING_BUTTON_PATTERN = /creating|submitting|saving|loading/i;

class B2bSetupSection extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.b2b-setup');
    if (!this.section) return;

    this.initHeliumFormModals();
    this.initStateRules();
  }

  isCustomerLoggedIn() {
    return this.section.dataset.customerLoggedIn === 'true';
  }

  getLoginUrl() {
    return this.section.dataset.loginUrl || '/account/login';
  }

  isDuplicateEmailError(errors) {
    if (!errors) return false;

    const messages = Array.isArray(errors) ? errors : [errors];
    return messages.some((error) => DUPLICATE_EMAIL_ERROR_PATTERN.test(String(error)));
  }

  isVerifyEmailError(errors) {
    if (!errors) return false;

    const messages = Array.isArray(errors) ? errors : [errors];
    return messages.some((error) => VERIFY_EMAIL_ERROR_PATTERN.test(String(error)));
  }

  getDuplicateEmailFieldMessage() {
    return 'This email is already registered. Log in to your account or use a different email address.';
  }

  isChallengeVerificationError(errors) {
    if (!errors) return false;

    const messages = Array.isArray(errors) ? errors : [errors];
    return messages.some((error) => CHALLENGE_ERROR_PATTERN.test(String(error)));
  }

  hasChallengeVerificationMessage(root) {
    if (!root) return false;

    return Array.from(root.querySelectorAll('.cf-field-error, .cf-error-message, .cf-form-error')).some(
      (errorEl) => CHALLENGE_ERROR_PATTERN.test(errorEl.textContent || '')
    );
  }

  hasVerifyEmailMessage(root) {
    return !!root?.querySelector('.cf-verify-email-message-wrapper, .cf-verify-email-message');
  }

  isFormSubmitting(formHost) {
    if (!formHost) return false;

    return Array.from(
      formHost.querySelectorAll('.cf-submit, .cf-save, .cf-next-step, .cf-submit-form, button[disabled]')
    ).some((button) => SUBMITTING_BUTTON_PATTERN.test(button.textContent || ''));
  }

  initHeliumFormModals() {
    this.section.querySelectorAll('[data-b2b-helium-modal]').forEach((modal) => {
      const formId = modal.dataset.formId;
      if (!formId) return;

      const formHost = modal.querySelector('[data-b2b-helium-form]');
      const getForm = () => modal.querySelector(`form[data-cf-form="${formId}"]`);

      const closeModal = () => {
        if (typeof modal.closeDialog === 'function') {
          modal.closeDialog();
        }
      };

      if (formHost) {
        formHost.addEventListener(
          'click',
          (event) => {
            const cancelBtn = event.target.closest('button.cf-cancel, a.cf-cancel');
            if (!cancelBtn || !formHost.contains(cancelBtn)) return;

            event.preventDefault();
            event.stopImmediatePropagation();
            closeModal();
          },
          true
        );

        formHost.addEventListener('input', () => {
          if (this.hasVerifyEmailMessage(formHost)) return;
          this.clearFormError(formHost);
        });
      }

      const waitForForm = () => {
        const run = () => {
          const form = getForm();
          if (!form?.querySelector('.cf-form-inner')) return;

          this.bindHeliumFormEvents(form, formHost);
          formMountObserver?.disconnect();
        };

        let formMountObserver;

        if (typeof window.CF?.ready === 'function') {
          window.CF.ready(run);
        } else {
          document.addEventListener('cf:ready', run, { once: true });
        }

        formMountObserver = new MutationObserver(run);
        if (formHost) {
          formMountObserver.observe(formHost, { childList: true, subtree: true });
        }

        run();
      };

      modal.addEventListener('dialog:open', waitForForm);
      waitForForm();
    });
  }

  bindHeliumFormEvents(form, formHost) {
    if (!formHost || form.dataset.b2bHeliumBound === 'true') return;

    form.dataset.b2bHeliumBound = 'true';

    form.addEventListener('cf:form_submitted', () => {
      if (this.isCustomerLoggedIn()) return;
      window.setTimeout(() => {
        if (!this.hasVerifyEmailMessage(formHost)) return;
        this.showExistingAccountEmailMessage(formHost, form, 'verify-email');
      }, 0);
    });

    form.addEventListener('cf:form_submission_failed', (event) => {
      const formErrors = event.detail?.errors;
      if (formErrors) {
        const flattenedErrors = Object.values(formErrors).flat();
        if (this.isChallengeVerificationError(flattenedErrors)) {
          this.showChallengeVerificationError(formHost);
          return;
        }
      }

      if (this.isCustomerLoggedIn()) return;

      const emailErrors = formErrors?.email;
      if (this.isDuplicateEmailError(emailErrors) || this.isVerifyEmailError(emailErrors)) {
        this.showExistingAccountEmailMessage(formHost, form, 'duplicate-email');
      }
    });

    form.addEventListener('cf:field_validated', (event) => {
      if (this.isFormSubmitting(formHost)) return;

      if (this.isChallengeVerificationError(event.detail?.errors)) {
        this.showChallengeVerificationError(formHost);
        return;
      }

      if (this.isCustomerLoggedIn()) return;
      if (event.detail?.columnKey !== 'email') return;

      if (
        this.isDuplicateEmailError(event.detail?.errors) ||
        this.isVerifyEmailError(event.detail?.errors)
      ) {
        this.showExistingAccountEmailMessage(formHost, form, 'duplicate-email');
      }
    });

    form.addEventListener('cf:step_changed', () => {
      if (this.hasVerifyEmailMessage(formHost)) return;
      this.clearFormError(formHost);
    });

    this.observeVerifyEmailState(form, formHost);
  }

  observeVerifyEmailState(form, formHost) {
    if (!formHost) return;

    const observer = new MutationObserver(() => {
      if (this.isCustomerLoggedIn() || this.isFormSubmitting(formHost)) return;
      if (!this.hasVerifyEmailMessage(formHost)) return;

      this.showExistingAccountEmailMessage(formHost, form, 'verify-email');
    });

    observer.observe(formHost, { childList: true, subtree: true });
  }

  showChallengeVerificationError(formHost) {
    if (!formHost || this.isFormSubmitting(formHost)) return;
    if (formHost.dataset.b2bErrorShown === 'challenge') return;

    const banner = this.ensureFormErrorBanner(formHost);
    banner.dataset.errorType = 'challenge';
    formHost.dataset.b2bErrorShown = 'challenge';
    banner.innerHTML = `
      <p class="b2b-setup__form-error-title">Security verification could not be completed</p>
      <p class="b2b-setup__form-error-text">
        This form uses Helium's invisible spam protection, so you usually will not see a checkbox.
        If verification fails, disable ad blockers, allow third-party cookies, refresh the page,
        and try again. If a challenge popup appears, complete it before submitting.
      </p>
    `;
  }

  showExistingAccountEmailMessage(formHost, form, context = 'duplicate-email') {
    if (!formHost || this.isCustomerLoggedIn() || this.isFormSubmitting(formHost)) return;
    if (formHost.dataset.b2bErrorShown === context) return;

    const loginUrl = this.getLoginUrl();
    const isVerifyState = context === 'verify-email';
    const banner = this.ensureFormErrorBanner(formHost);

    banner.dataset.errorType = 'existing-email';
    formHost.dataset.b2bErrorShown = context;

    if (isVerifyState) {
      banner.innerHTML = `
        <p class="b2b-setup__form-error-title">This email is already registered</p>
        <p class="b2b-setup__form-error-text">
          An account with this email already exists in our store. Because you submitted this form
          while not logged in, a verification email was sent to confirm you own this address.
        </p>
        <ul class="b2b-setup__form-error-list">
          <li><strong>Fastest option:</strong> log in to your existing account, then open this form again and submit.</li>
          <li><strong>If this is your email:</strong> check your inbox and spam folder for the verification link.</li>
          <li><strong>If this is not your email:</strong> go back and use a different email address.</li>
        </ul>
        <a class="b2b-setup__form-error-login" href="${this.escapeHtml(loginUrl)}">Log in to your account</a>
      `;
      formHost.classList.add('b2b-setup__gov-form-host--verify-email');
      this.replaceVerifyEmailMessage(form);
    } else {
      banner.innerHTML = `
        <p class="b2b-setup__form-error-title">This email is already registered</p>
        <p class="b2b-setup__form-error-text">
          An account with this email already exists. Log in to continue with your existing account,
          or use a different email address.
        </p>
        <a class="b2b-setup__form-error-login" href="${this.escapeHtml(loginUrl)}">Log in to your account</a>
      `;
      this.replaceEmailFieldErrors(form, this.getDuplicateEmailFieldMessage());
    }
  }

  ensureFormErrorBanner(formHost) {
    let banner = formHost.querySelector('.b2b-setup__form-error');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'b2b-setup__form-error';
      banner.setAttribute('role', 'alert');
      formHost.prepend(banner);
    }

    return banner;
  }

  replaceVerifyEmailMessage(form) {
    const textEl = form?.querySelector('.cf-verify-email-message-text');
    if (!textEl) return;

    textEl.textContent =
      'A verification email was sent because this address is already on file. Log in to your existing account for the fastest way to continue.';
  }

  clearFormError(formHost) {
    formHost?.querySelector('.b2b-setup__form-error')?.remove();
    formHost?.classList.remove('b2b-setup__gov-form-host--verify-email');
    if (formHost?.dataset) {
      delete formHost.dataset.b2bErrorShown;
    }
  }

  getEmailField(form) {
    return (
      form?.querySelector('.cf-field[data-cf-column-key="email"]') ||
      form?.querySelector('.cf-field-container[data-cf-column-key="email"] .cf-field') ||
      form?.querySelector('.cf-field[data-cf-field-type="email"]')
    );
  }

  replaceEmailFieldErrors(form, message) {
    const emailField = this.getEmailField(form);
    if (!emailField) return;

    emailField.querySelectorAll('.cf-field-error, .cf-error-message').forEach((errorEl) => {
      errorEl.textContent = message;
    });
  }

  async initStateRules() {
    const container = this.section.querySelector('[data-b2b-state-rules]');
    if (!container) return;

    const url = container.dataset.rulesUrl;
    if (!url) return;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load state rules');
      const states = await response.json();
      container.innerHTML = this.buildColumns(states);
      this.bindAccordions(container);
    } catch (error) {
      console.error(error);
      container.innerHTML =
        '<p class="b2b-setup__rules-error">Unable to load state exemption rules. Please refresh the page.</p>';
    }
  }

  buildColumns(states) {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const columnCount = isMobile ? 1 : 3;
    const columns = Array.from({ length: columnCount }, () => []);

    states.forEach((state, index) => {
      columns[index % columnCount].push(this.buildStateItem(state));
    });

    return columns
      .map(
        (column) => `
        <div class="b2b-setup__rules-column">
          ${column.join('')}
        </div>
      `
      )
      .join('');
  }

  buildStateItem(state) {
    const sections = [];

    if (state.intro) {
      sections.push(`<p class="b2b-setup__state-intro">${this.escapeHtml(state.intro)}</p>`);
    }

    if (state.expiration) {
      sections.push(`
        <div>
          <div class="b2b-setup__rules-label">Expiration period</div>
          <p>${this.escapeHtml(state.expiration)}</p>
        </div>
      `);
    }

    if (state.forms?.length) {
      sections.push(`
        <div>
          <div class="b2b-setup__rules-label">Forms</div>
          <ul>${state.forms.map((link) => this.buildLink(link)).join('')}</ul>
        </div>
      `);
    }

    if (state.references?.length) {
      const label = state.references.length > 1 ? 'References' : 'Reference';
      sections.push(`
        <div>
          <div class="b2b-setup__rules-label">${label}</div>
          <ul>${state.references.map((link) => this.buildLink(link)).join('')}</ul>
        </div>
      `);
    }

    return `
      <details class="b2b-setup__state">
        <summary class="b2b-setup__state-trigger">
          <span>${this.escapeHtml(state.name)}</span>
          <svg class="b2b-setup__state-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"/>
          </svg>
        </summary>
        <div class="b2b-setup__state-panel">
          ${sections.join('')}
        </div>
      </details>
    `;
  }

  buildLink(link) {
    return `
      <li>
        <a href="${this.escapeHtml(link.url)}" target="_blank" rel="noopener noreferrer">
          ${this.escapeHtml(link.label)}
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M4.5 2H2v8h8V7.5M6.5 1.5h4m0 0v4m0-4L6 6" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"/>
          </svg>
        </a>
      </li>
    `;
  }

  bindAccordions(container) {
    container.querySelectorAll('.b2b-setup__state').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        const scope = details.closest('.b2b-setup__rules-grid') || this.section;
        scope?.querySelectorAll('.b2b-setup__state[open]').forEach((other) => {
          if (other !== details) other.open = false;
        });
      });
    });
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('b2b-setup-section', B2bSetupSection);

document.querySelectorAll('.b2b-setup').forEach((section) => {
  if (!section.querySelector('b2b-setup-section')) {
    const enhancer = document.createElement('b2b-setup-section');
    section.appendChild(enhancer);
  }
});
